import { css } from 'styled-components';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { Brand } from '@/domainLogic/index.ts';
import type React from 'react';
import type { ControlProps } from '../../../../Form';
import { getDisabled } from '../../../../Modal/Modals/FiltersModal/utils';

export const HEIGHT_OF_DESKTOP_HEADER = 136;
const HEIGHT_OF_ACTION_BAR = 72;
const DEFAULT_MAX_HEIGHT = 496;
const PAGE_HEIGHT = '100vh';

export const FILTERS_THAT_CAN_FILTER_ITSELF = [
  URLParameters.ACCOMODATION_FACILITIES,
  URLParameters.GENERAL_ACCOMODATION_FACILITIES,
  URLParameters.FAMILY_ACCOMODATION_FACILITIES,
  URLParameters.ACTIVITY_ACCOMODATION_FACILITIES,
  URLParameters.WELLNESS_ACCOMODATION_FACILITIES,
  URLParameters.FOOD_ACCOMODATION_FACILITIES,
];

export const disableControls = (controls: ControlProps[]) =>
  controls.map((x) => ({
    ...x,
    options: x.options?.map((y) => ({
      ...y,
      disabled: true,
    })),
  }));

export const getCurrentFilterControl = (filters: ControlProps[], name: string | undefined): ControlProps[] =>
  filters?.filter((x) => x.name === name)?.[0]?.controls || [];

const getControlFromName = (controls: ControlProps[], name?: string) =>
  controls?.filter((control) => control?.name === name);

const getAddLabel = (value: number, controls: ControlProps[], ControlName?: string) => {
  const currentContainer = getControlFromName(controls, ControlName);
  const currentOption = currentContainer?.[0]?.options?.filter((option) => option?.value === value)?.[0];
  const currentAddLabel = currentOption?.addLabel;

  return {
    addLabel: currentAddLabel,
    disabled: getDisabled(currentOption, currentAddLabel),
  };
};

export const updateFiltersWithCurrentOrder = (controls: ControlProps[], newControls: ControlProps[]): ControlProps[] =>
  controls.map((x) => ({
    ...x,
    options: x.options?.map((y) => ({
      ...y,
      ...getAddLabel(y.value, newControls, x?.name),
    })),
  }));

export const getAppliedFilters = (appliedFilters: SearchParams, filterName: string): boolean => {
  if (filterName === URLParameters.ACCOMODATION_FACILITIES) {
    return (
      !!appliedFilters?.[URLParameters.GENERAL_ACCOMODATION_FACILITIES] ||
      !!appliedFilters?.[URLParameters.FAMILY_ACCOMODATION_FACILITIES] ||
      !!appliedFilters?.[URLParameters.ACTIVITY_ACCOMODATION_FACILITIES] ||
      !!appliedFilters?.[URLParameters.WELLNESS_ACCOMODATION_FACILITIES] ||
      !!appliedFilters?.[URLParameters.FOOD_ACCOMODATION_FACILITIES]
    );
  } else {
    return !!appliedFilters?.[filterName];
  }
};

const getOuterHeight = () => `calc(${PAGE_HEIGHT} - ${HEIGHT_OF_DESKTOP_HEADER}px)`;
const getInnerHeight = () => `calc(${PAGE_HEIGHT} - ${HEIGHT_OF_DESKTOP_HEADER}px - ${HEIGHT_OF_ACTION_BAR}px)`;

export const maxHeightOfOverlay = css<{
  hasScroll: boolean;
  brand: Brand;
}>`
  ${({ hasScroll }) => css`
    max-height: min(${getOuterHeight()}, ${DEFAULT_MAX_HEIGHT}px);

    .oss-ui-form {
      ${
        hasScroll &&
        css`
          margin-bottom: 56px;
        `
      };
      max-height: min(${getInnerHeight()}, calc(${DEFAULT_MAX_HEIGHT}px - ${HEIGHT_OF_ACTION_BAR}px));
    }
  `}}
`;

export const positionOverlayBelowButton = (
  refEl: HTMLElement | null,
  overlayEl: HTMLElement | null,
  isFixed: boolean
) => {
  if (!refEl || !overlayEl) return;
  const rect = refEl.getBoundingClientRect();
  const styles = overlayEl?.style;

  if (styles) {
    if (isFixed) {
      Object.assign(styles, {
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        right: 'unset',
        bottom: 'unset',
        zIndex: '100',
      });
    } else {
      Object.assign(styles, {
        position: 'fixed',
        top: `${rect.bottom + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        right: 'unset',
        bottom: 'unset',
        zIndex: '100',
      });
    }
  }
};

export const isRightSideOutsideOfWindow = (ref: React.RefObject<HTMLDivElement | null>) => {
  const bounding = ref?.current?.getBoundingClientRect();
  const innerWidth = window.innerWidth || document.documentElement.clientWidth;
  return (bounding?.right || 0) >= innerWidth;
};

export const positionOverlayToTheRight = (overlayEl: HTMLElement | null) => {
  if (!overlayEl) return;
  const styles = overlayEl?.style;

  if (styles) {
    styles.right = '16px';
    styles.left = 'auto';
  }
};
